import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { QUERY_KEYS } from '@constants/queryKeys';
import { IUser, useAuthContext } from '@auth_module/context/AuthContext';
import {
  UserApplication,
  fetchFeatureFlags,
  fetchUserApplications,
  fetchUserInfo,
  fetchUserOrganization,
} from '@auth_module/services';
import { IFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { useNotification } from '@insights_module/hooks/useNotification';
import { Organization } from '@app/types/ADCTypes';
import { IRequestError } from '@configs/axios-instances';

export function useUser() {
  const { auth } = useAuthContext();
  const [isCastedUserReady, setIsCastedUserReady] = useState(false);
  const { notify, NTFC_TYPE } = useNotification();

  const { data, isFetching, isLoading, error } = useQuery({
    queryKey: [QUERY_KEYS.ME],
    queryFn: fetchUserInfo,
    enabled: !!auth.token,
    // on refetch user data app is reloading,
    // so we need to set staleTime long or app will be reloading every 5 minutes (default value)
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const { data: features, isLoading: isLoadingUserFeatures } = useQuery({
    queryKey: [QUERY_KEYS.FEATURE_FLAGS],
    queryFn: fetchFeatureFlags,
    enabled: !!auth.token,
    staleTime: 60 * 60 * 1000, // 60 minutes
  });

  const isOrganizationRequestReady = !!auth.token && !!data?.data?.organization_id;

  const { data: organization, isLoading: isLoadingOrganization } = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION, { id: data?.data?.organization_id }],
    queryFn: () => fetchUserOrganization(data?.data?.organization_id),
    enabled: isOrganizationRequestReady,
    staleTime: 60 * 60 * 1000, // 60 minutes
  });

  const { data: userApplications, isLoading: isLoadingUserApplications } = useQuery({
    queryKey: [QUERY_KEYS.USER_APPLICATIONS, { id: data?.data?.id }],
    queryFn: () => fetchUserApplications(),
    enabled: !!auth.token,
    staleTime: 60 * 60 * 1000, // 60 minutes
  });

  useEffect(() => {
    if (auth.castedAdmin && data?.data?.email) {
      setIsCastedUserReady(true);
    }
  }, [data?.data?.email]);

  const notifyCastedUser = useCallback(() => {
    if (isCastedUserReady) {
      notify({
        type: NTFC_TYPE.WARNING,
        message: `Admin user with email ${auth.castedAdmin} casted as user with email ${data?.data?.email}`,
        duration: 30000,
      });
    }
  }, [isCastedUserReady]);

  return {
    user: data?.data as IUser | undefined,
    isFetching,
    isLoading,
    userFeatures: features?.data as IFeatureFlag[],
    isLoadingUserFeatures,
    isCastedUserReady,
    notifyCastedUser,
    organization: organization?.data as Organization,
    isLoadingOrganization: isLoadingOrganization && isOrganizationRequestReady,
    userApplications: userApplications?.data as UserApplication[] | undefined,
    isLoadingUserApplications,
    error: error as IRequestError | null,
  };
}
